// src/pages/auth/ActionHandler.jsx
//
// Handles Firebase Auth's `mode` action links — the same URL shape
// regardless of whether the user clicked "verify email", "recover
// email", or "reset password" from a transactional email. Reads the
// `mode` + `oobCode` from the query string and runs the right SDK
// call. Renders one of four states:
//
//   working          — running the SDK call (verifyEmail, etc.)
//   success          — done, redirecting via continueUrl
//   error            — code invalid/expired, escape hatch back to /login
//   awaitingPassword — only for resetPassword: show the new-password form
'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getAuth,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
  AuthBanner,
  authInputClass,
  authLabelClass,
} from '@/components/auth/AuthShell';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ActionHandler() {
  const auth = getAuth();
  const navigate = useNavigate();
  const q = useQuery();

  const mode = q.get('mode');           // verifyEmail | resetPassword | recoverEmail
  const oobCode = q.get('oobCode');
  const continueUrl = q.get('continueUrl') || '/login';

  const [state, setState] = useState({ status: 'idle', message: '' });
  const [newPassword, setNewPassword] = useState('');
  const [pwEmail, setPwEmail] = useState('');

  useEffect(() => {
    let cancel = false;
    async function run() {
      if (!mode || !oobCode) {
        setState({ status: 'error', message: 'Missing action parameters.' });
        return;
      }

      if (mode === 'verifyEmail') {
        setState({ status: 'working', message: 'Verifying your email…' });
        try {
          await applyActionCode(auth, oobCode);
          await auth.currentUser?.reload?.();
          if (!cancel) {
            setState({ status: 'success', message: 'Email verified! Redirecting…' });
            setTimeout(() => navigate(continueUrl, { replace: true }), 1200);
          }
        } catch {
          if (!cancel) setState({ status: 'error', message: 'Verification link is invalid or expired.' });
        }
      } else if (mode === 'recoverEmail') {
        setState({ status: 'working', message: 'Restoring your email…' });
        try {
          await applyActionCode(auth, oobCode);
          if (!cancel) {
            setState({ status: 'success', message: 'Email restored. You can now sign in.' });
            setTimeout(() => navigate('/login', { replace: true }), 1200);
          }
        } catch {
          if (!cancel) setState({ status: 'error', message: 'Recovery link is invalid or expired.' });
        }
      } else if (mode === 'resetPassword') {
        setState({ status: 'working', message: 'Validating reset link…' });
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancel) {
            setPwEmail(email);
            setState({ status: 'awaitingPassword', message: '' });
          }
        } catch {
          if (!cancel) setState({ status: 'error', message: 'Reset link is invalid or expired.' });
        }
      } else {
        setState({ status: 'error', message: 'Unsupported action.' });
      }
    }
    run();
    return () => { cancel = true; };
  }, [auth, mode, oobCode, continueUrl, navigate]);

  async function submitNewPassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setState({ status: 'awaitingPassword', message: 'Password must be at least 8 characters.' });
      return;
    }
    try {
      setState({ status: 'working', message: 'Updating your password…' });
      await confirmPasswordReset(getAuth(), oobCode, newPassword);
      setState({ status: 'success', message: 'Password updated. Redirecting to sign in…' });
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch {
      setState({ status: 'error', message: 'Could not set password. Try the link again.' });
    }
  }

  // ── Pill + heading vary by mode + state ───────────────────────────
  const pillLabel =
    state.status === 'success' ? 'Done'
    : state.status === 'error' ? 'Link issue'
    : mode === 'resetPassword' ? 'Reset password'
    : mode === 'recoverEmail' ? 'Email recovery'
    : 'Verify email';

  return (
    <AuthShell cardWidth="narrow">
      <div className="text-center">
        <AuthPill>{pillLabel}</AuthPill>

        {/* Working spinner */}
        {(state.status === 'idle' || state.status === 'working') && (
          <>
            <div className="mx-auto mt-6 mb-2 h-12 w-12">
              <svg
                className="h-12 w-12 animate-spin text-[#b5f03d]"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <AuthHeading size="small">
              One <span className="text-[#b5f03d]">moment.</span>
            </AuthHeading>
            <AuthSubtitle>{state.message || 'Working on it…'}</AuthSubtitle>
          </>
        )}

        {/* Success */}
        {state.status === 'success' && (
          <>
            <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <svg
                className="h-8 w-8 text-[#b5f03d]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <AuthHeading size="small">
              All <span className="text-[#b5f03d]">set.</span>
            </AuthHeading>
            <AuthSubtitle>{state.message}</AuthSubtitle>
          </>
        )}

        {/* Error — recoverable amber, not red */}
        {state.status === 'error' && (
          <>
            <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/40">
              <svg
                className="h-8 w-8 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <AuthHeading size="small">
              Link <span className="text-[#b5f03d]">expired.</span>
            </AuthHeading>
            <AuthSubtitle>{state.message}</AuthSubtitle>
            <div className="mt-7">
              <AuthButton type="button" onClick={() => navigate('/login')}>
                Back to sign in
              </AuthButton>
            </div>
          </>
        )}

        {/* Password reset form */}
        {state.status === 'awaitingPassword' && (
          <>
            <AuthHeading size="small">
              Set a new <span className="text-[#b5f03d]">password.</span>
            </AuthHeading>
            <AuthSubtitle>
              Choose a new password for <strong className="text-white">{pwEmail}</strong>.
            </AuthSubtitle>
          </>
        )}
      </div>

      {state.status === 'awaitingPassword' && (
        <form onSubmit={submitNewPassword} className="mt-7 space-y-4 text-left" noValidate>
          <div className="flex flex-col gap-1.5">
            <label className={authLabelClass} style={{ fontFamily: HEAD_FONT }}>
              Email
            </label>
            <input
              value={pwEmail}
              disabled
              className={`${authInputClass} opacity-60`}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-password"
              className={authLabelClass}
              style={{ fontFamily: HEAD_FONT }}
            >
              New password
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoFocus
              autoComplete="new-password"
              minLength={8}
              required
              className={authInputClass}
            />
          </div>

          {state.message && (
            <AuthBanner tone="error">{state.message}</AuthBanner>
          )}

          <AuthButton type="submit" className="mt-2">
            Set password
          </AuthButton>
        </form>
      )}
    </AuthShell>
  );
}
